import React, {useCallback, useEffect, useState} from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import { useAuth } from "hooks/useAuth";
import { niceUserName } from "utils/functions";
import CustomDataTable from "shared/tables/CustomDataTable";
import BoxContent from "shared/ui/BoxContent";
import PageTitle from "shared/ui/PageTitle";
import GenericButton from "shared/ui/GenericButton";
import {APIGetProfiles, APIPostProfileLogin} from "../api/profiles";
import useDataTable from "../../../hooks/useDataTable";

interface Profile {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    [key: string]: any;
}

const TABLE_COLUMNS = [
    { id: "last_name", label: "Nume și prenume", noWrap: true, },
    { id: "email", label: "E-mail", disablePadding: false, noWrap: true, },
    { id: "actions", label: "Acțiuni", numeric: true, sticky: true },
]

const Profiles = () => {
    const { update, login, user } = useAuth();
    const navigate = useNavigate();
    const [principal, setPrincipal] = useState<Profile[]>([]);

    const {
        loading,
        setLoading,
        tableData,
        setTableData,
    } = useDataTable("", "", undefined, "");

    const mapUserProfile = (user: Profile) => {
        return {
            last_name: niceUserName(user.first_name, user.last_name, user.email),
            email: user.email,
            actions: (
                <Box display="flex" justifyContent="end" mr={1}>
                    <GenericButton type="switch-user" onClick={() => switchProfileHandler(user)} />
                </Box>
            ),
        }
    }

    useEffect(() => {
        setLoading(true);
        APIGetProfiles()
            .then((response) => {
                if (response.data.success) {
                    // console.log("getTeachers OK", response);
                    setTableData(response.data.data.teachers.map((user: Profile) => mapUserProfile(user)));
                    setPrincipal(response.data.data.principal.map((user: Profile) => mapUserProfile(user)));
                } else {
                    update("error", { show: true, code: "A002" });
                }
            })
            .catch(() => {
                // console.log("getTeachers ERR", err);
                update("error", { show: true, code: "A001" });
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const mapLoginData = (data: any) => ({
        firstname: data.first_name || "",
        lastname: data.last_name || "",
        email: data.email || "",
        position: data.position || "",
        profilePhoto: data.profile_photo || "",
        token: `Bearer ${data.token}`,
        menu: data.menu || [],
        emailVerified: data.email_verified,
        id: data.id,
        notifications: data.notifications_unread,
        schoolBoard: data.has_school_board,
        swap: data.can_swap_profile,
        testShareLink: data.test_share_link ?? false,
        testsListingShow: data.tests_listing_show_report_and_score ?? true,
        tenantNotifications: data?.tenant_notifications ?? false,
        emailNotifications: data?.email_notifications ?? false,
    });

    const switchProfileHandler = useCallback((profile: Profile) => {
        update("loading", { show: true, modal: true });

        const isCurrentUser = user.email === profile.email && !!user.firstname && user.emailVerified !== null;

        if (isCurrentUser) {
            navigate("/statistici");
            update("loading", { show: false });
        } else {
            APIPostProfileLogin({ user_id: profile.id })
                .then((response) => {
                    if (response.data.success && response.data.data?.token) {
                        login(mapLoginData(response.data.data));
                        if (response.data.data.email_verified === null) {
                            navigate("/verifica-email");
                        }
                    } else {
                        update("error", { show: true, code: "A002" });
                    }
                })
                .catch(() => update("error", { show: true, code: "A001" }))
                .finally(() => update("loading", { show: false }));
        }
    }, [navigate, login, update, user.email, user.firstname]);

    const renderProfileTable = (data: Profile[]) => {

        return (
            <CustomDataTable
                loading={loading}
                columns={TABLE_COLUMNS}
                rows={data}
                page={1}
                pagination={false}
            />
        );
    };

    return (
        <Box>
            <PageTitle>Director</PageTitle>
            <BoxContent>{renderProfileTable(principal)}</BoxContent>
            <Box mb={3} />
            <PageTitle>Profesori</PageTitle>
            <BoxContent>
                {renderProfileTable(tableData)}
            </BoxContent>
        </Box>
    );
};

export default Profiles;
